import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useReminderFirebase } from './services/reminderFirebase';
import ReminderPopup from './ReminderPopup';
import type { Reminder, ReminderResponse } from './types/reminder';
import { MAX_SNOOZE_COUNT, SNOOZE_DELAY_MS } from './types/reminder';

type Trigger = {
  reminderId: string;
  message: string;
  studentId: string;
  snoozeCount: number;
  isSoft: boolean;
};

type PostSnoozeGuard = {
  reminderId: string;
  studentId: string;
  notBefore: number;
};

type ReminderStudentPopupsProps = {
  classId: string;
  studentId: string;
  isStudentActive?: boolean;
};

export default function ReminderStudentPopups({
  classId,
  studentId,
  isStudentActive = true,
}: ReminderStudentPopupsProps) {
  const { subscribeRemindersForStudent, subscribeResponsesForStudent, setReminderResponse } =
    useReminderFirebase();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [responses, setResponses] = useState<Record<string, ReminderResponse>>({});
  const [responsesReady, setResponsesReady] = useState(false);
  const [activePopup, setActivePopup] = useState<Trigger | null>(null);
  const timeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const postSnoozeNotBeforeRef = useRef<PostSnoozeGuard | null>(null);
  const responsesRef = useRef(responses);
  responsesRef.current = responses;
  const isReminderForToday = (reminder: Reminder) => {
    if (reminder.reminderAt) {
      return dayjs(reminder.reminderAt).isSame(dayjs(), 'day');
    }
    return dayjs(reminder.createdAt || reminder.finalReminderTime).isSame(dayjs(), 'day');
  };

  useEffect(() => {
    if (!classId || !studentId || !isStudentActive) {
      setReminders([]);
      setResponses({});
      setResponsesReady(false);
      setActivePopup(null);
      return () => {};
    }
    setResponsesReady(false);
    const unsubReminders = subscribeRemindersForStudent(classId, studentId, setReminders);
    const unsubResponses = subscribeResponsesForStudent(studentId, (nextResponses) => {
      setResponses(nextResponses);
      setResponsesReady(true);
    });
    return () => {
      unsubReminders();
      unsubResponses();
    };
  }, [classId, studentId, isStudentActive]);

  useEffect(() => {
    if (!isStudentActive) return () => {};
    timeoutsRef.current.forEach((t) => clearTimeout(t));
    timeoutsRef.current = [];
    if (!responsesReady) return () => {};
    const now = Date.now();

    const isSnoozeCooldownActiveFor = (remId: string) => {
      const g = postSnoozeNotBeforeRef.current;
      return Boolean(
        g &&
          g.reminderId === remId &&
          g.studentId === studentId &&
          Date.now() < g.notBefore
      );
    };

    reminders.forEach((rem) => {
      if (rem.status !== 'active') return;
      if (!isReminderForToday(rem)) {
        console.log('Skipping reminder popup (not today):', {
          reminderAt: rem.reminderAt,
        });
        return;
      }
      const resp = responses[rem.reminderId];
      if (resp?.status === 'dismissed') return;

      const snoozeCount = resp?.snoozeCount ?? 0;
      const actionTime = resp?.actionTime ?? null;

      if (resp?.status === 'snoozed') {
        const guard = postSnoozeNotBeforeRef.current;
        if (
          actionTime == null &&
          !(guard && guard.reminderId === rem.reminderId && guard.studentId === studentId)
        ) {
          return;
        }
        const nextAtRaw =
          actionTime != null
            ? actionTime + SNOOZE_DELAY_MS
            : guard && guard.reminderId === rem.reminderId && guard.studentId === studentId
              ? guard.notBefore
              : now + SNOOZE_DELAY_MS;
        const nextAt =
          guard && guard.reminderId === rem.reminderId && guard.studentId === studentId
            ? Math.max(nextAtRaw, guard.notBefore)
            : nextAtRaw;
        if (nextAt > now) {
          const delay = nextAt - now;
          const t = setTimeout(() => {
            postSnoozeNotBeforeRef.current = null;
            setActivePopup({
              reminderId: rem.reminderId,
              message: rem.message,
              studentId,
              snoozeCount,
              isSoft: false,
            });
          }, delay);
          timeoutsRef.current.push(t);
        } else {
          postSnoozeNotBeforeRef.current = null;
          setActivePopup({
            reminderId: rem.reminderId,
            message: rem.message,
            studentId,
            snoozeCount,
            isSoft: false,
          });
        }

        return;
      }

      if (rem.softReminderTime != null) {
        if (rem.softReminderTime > now) {
          const delay = rem.softReminderTime - now;
          const remId = rem.reminderId;
          const t = setTimeout(() => {
            if (isSnoozeCooldownActiveFor(remId)) return;
            const latest = responsesRef.current[remId];
            if (latest?.status === 'snoozed' || latest?.status === 'dismissed') return;
            setReminderResponse(remId, studentId, {
              status: 'soft_reminder_shown',
              snoozeCount: latest?.snoozeCount ?? 0,
            });
            setActivePopup({
              reminderId: remId,
              message: rem.message,
              studentId,
              snoozeCount: latest?.snoozeCount ?? 0,
              isSoft: true,
            });
          }, delay);
          timeoutsRef.current.push(t);
        } else if (!resp || resp.status === 'pending') {
          if (isSnoozeCooldownActiveFor(rem.reminderId)) return;
          setReminderResponse(rem.reminderId, studentId, {
            status: 'soft_reminder_shown',
            snoozeCount: resp?.snoozeCount ?? 0,
          });
          setActivePopup({
            reminderId: rem.reminderId,
            message: rem.message,
            studentId,
            snoozeCount: resp?.snoozeCount ?? 0,
            isSoft: true,
          });
        }
      }

      if (rem.finalReminderTime > now) {
        const delay = rem.finalReminderTime - now;
        const remId = rem.reminderId;
        const t = setTimeout(() => {
          if (isSnoozeCooldownActiveFor(remId)) return;
          const latest = responsesRef.current[remId];
          if (latest?.status === 'snoozed' || latest?.status === 'dismissed') return;
          setReminderResponse(remId, studentId, {
            snoozeCount: latest?.snoozeCount ?? 0,
          });
          setActivePopup({
            reminderId: remId,
            message: rem.message,
            studentId,
            snoozeCount: latest?.snoozeCount ?? 0,
            isSoft: false,
          });
        }, delay);
        timeoutsRef.current.push(t);
      } else if (!resp || resp.status === 'pending' || resp.status === 'soft_reminder_shown') {
        if (isSnoozeCooldownActiveFor(rem.reminderId)) return;
        setReminderResponse(rem.reminderId, studentId, {
          snoozeCount: resp?.snoozeCount ?? 0,
        });
        setActivePopup({
          reminderId: rem.reminderId,
          message: rem.message,
          studentId,
          snoozeCount: resp?.snoozeCount ?? 0,
          isSoft: false,
        });
      }
    });

    return () => {
      timeoutsRef.current.forEach((t) => clearTimeout(t));
      timeoutsRef.current = [];
    };
  }, [reminders, responses, responsesReady, studentId, isStudentActive]);

  const handleSnooze = (reminderId: string, sid: string) => {
    const resp = responses[reminderId];
    const current = resp?.snoozeCount ?? 0;
    if (current >= MAX_SNOOZE_COUNT) return;
    postSnoozeNotBeforeRef.current = {
      reminderId,
      studentId: sid,
      notBefore: Date.now() + SNOOZE_DELAY_MS,
    };
    setReminderResponse(reminderId, sid, {
      status: 'snoozed',
      snoozeCount: current + 1,
    });
    setActivePopup(null);
  };

  const handleDismiss = (reminderId: string, sid: string) => {
    postSnoozeNotBeforeRef.current = null;
    setReminderResponse(reminderId, sid, { status: 'dismissed' });
    setActivePopup(null);
  };

  if (!activePopup) return null;

  return (
    <ReminderPopup
      message={activePopup.message}
      reminderId={activePopup.reminderId}
      studentId={activePopup.studentId}
      onSnooze={handleSnooze}
      onDismiss={handleDismiss}
      snoozeCount={activePopup.snoozeCount}
      maxSnooze={MAX_SNOOZE_COUNT}
    />
  );
}
